import { useQuery } from '@tanstack/react-query';
import type { Product } from '@atlas/shared';
import { api } from '@/services/api';

/**
 * The product list behind the intake pickers — every visible product, name only.
 *
 * Shared by every product picker (New Evaluation, New Task (ENG) and (TW),
 * bulletins, marketing requests) under one query key so opening one after the
 * other is free.
 *
 * Cached far longer than the app-wide default (60s). This is ~1,570 rows fetched
 * only to populate a dropdown, and the catalogue changes a few times a week at
 * most, so refetching it every time someone opens a form is pure waste. The key
 * sits under ['products'], so a product created, renamed or hidden in this tab
 * invalidates it with everything else; before that, a new product could be
 * missing from the picker for half an hour (#1277). One made in another tab
 * still waits out the cache.
 */
export function useProductOptions() {
  const query = useQuery({
    queryKey: ['products', 'lite', 'metadata.name'],
    queryFn: () => api.products.listLite(['metadata.name']),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    products: (query.data ?? []) as Product[],
    isLoading: query.isLoading,
  };
}
